import { inject, Injectable } from '@angular/core';
import { ListLayout, ListLayoutMultipleComponent } from 'gung-list';
import { Product, ProductListByIdsConfigService, SelectedCustomerService } from 'gung-standard';
import { environment } from 'projects/car-liner/src/environments/environment';
import { BehaviorSubject, defer, first, forkJoin, map, of, switchMap } from 'rxjs';
import { CarLinerIncludedItemsGridViewComponent } from '../components/car-liner-included-items-grid-view/car-liner-included-items-grid-view.component';
import { CarLinerIncludedItemsTableComponent } from '../components/car-liner-included-items-table/car-liner-included-items-table.component';

@Injectable()
export class CarLinerIncludedItemsByIdsService extends ProductListByIdsConfigService {
  protected selectedCustomerSerice = inject(SelectedCustomerService);

  parentIdSubject = new BehaviorSubject<string>('');
  isBlackhawkProject = environment.projectName === 'blackhawk' ? true : false;

  setParentId(id: string) {
    this.parentIdSubject.next(id);
  }

  getItems() {
    return this.parentIdSubject.asObservable().pipe(
      switchMap(parentId =>
        forkJoin([
          this.productService.getProduct(parentId),
          this.productIdsSubject.asObservable().pipe(first()),
          this.selectedCustomerSerice.getSelectedCustomer().pipe(first())
        ])
      ),
      switchMap(([parentProduct, productIds, selectedCustomer]) => {
        return forkJoin({
          parentProduct: of(parentProduct),
          productIds: of(productIds),
          getProductsByAssortment: defer(() =>
            this.productService
              .getProductsByAssortment(
                this.isBlackhawkProject
                  ? 'BLACKHAWK_' + selectedCustomer.extra.kus.prislista
                  : selectedCustomer.extra.kus.prislista
              )
              .pipe(first())
          )
        })
      }),
      switchMap(({ parentProduct, productIds, getProductsByAssortment }) => {
        const bantalaviMap = this.createSrBantalaviMap(parentProduct);
        let productsToGet = [];
        for (const id of productIds) {
          const product = getProductsByAssortment.find(p => p.id === id);
          if (product) {
            productsToGet.push(product.id);
          }
        }
        return this.productService.getProductsByIds(productsToGet).pipe(
          map(products => {
            return products.map(product => {
              if (bantalaviMap.has(product.id)) {
                product.extra._sr_bantalavi = bantalaviMap.get(product.id);
              }
              product.extra._parentProduct = parentProduct;
              return product;
            }).sort((a, b) => {
              // Helper function to get the part of id before "-"
              const extractId = (id) => {
                const parts = id.split("-");
                return parts[0]; // Take everything before "-"
              };

              const idA = extractId(a.id);
              const idB = extractId(b.id);

              // Helper to extract numeric part at the beginning of the ID
              const getNumericPart = (id) => parseInt(id, 10) || 0; // Extracts leading number, defaults to 0 if not numeric

              // Extract the numeric parts
              const numericA = getNumericPart(idA);
              const numericB = getNumericPart(idB);

              // Primary sort by numeric part
              if (numericA !== numericB) {
                return numericA - numericB;
              }

              // Secondary sort lexicographically if numeric parts are equal
              return idA.localeCompare(idB);
            });
          })
        );
      })
    );
  }

  private createSrBantalaviMap(product: Product): Map<string, number> {
    const result = new Map<string, number>();
    if (product?.extra?.sr) {
      const sr = product.extra.sr;
      for (const srItem of sr) {
        const includedProductId = srItem.bartnrstrukt;
        const includedProductUnits = srItem.bantalavi;

        result.set(includedProductId, includedProductUnits);
      }
    }

    return result;
  }

  getLayouts(): ListLayout<Product>[] {
    return [
      {
        getIconClass: () => 'fa fa-th',
        getListItemComponent: () => CarLinerIncludedItemsGridViewComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductGrid'
      },
      {
        getIconClass: () => 'fa fa-list',
        getListItemComponent: () => CarLinerIncludedItemsTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductList'
      }
    ];
  }
}
